import { Injectable, Optional, SkipSelf } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { User } from '../models/user.model';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { SessionWarningComponent } from '../components/session-warning/session-warning.component';
import { MatDialog } from '@angular/material/dialog';
import { JwtResponse } from '../models/jwt-response.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  SESSION_TIMEOUT_WARNING = 1000 * 60 * 5; // last 5 minutes
  sessionExpirationTime!: number;

  warningTimeout!: any;
  logoutTimeout!: any;

  BASE_URI = environment.apiBaseUrl;
  AUTH_URI = this.BASE_URI + '/auth';
  USERS_URI = this.BASE_URI + '/users';

  userSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  user$: Observable<User | null> = this.userSubject.asObservable();

  authenticatedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  authenticated$: Observable<boolean> = this.authenticatedSubject.asObservable();

  constructor(
    private _httpClient: HttpClient,
    private _router: Router,
    private _dialog: MatDialog,

    @Optional() @SkipSelf() private service?: AuthService
  ) {
    if (service) {
      throw new Error('AuthService is already loaded.');
    }

    this.clearSessionTimers();
  }

  public login(username: string, password: string, rememberMe = false, institutionId: number) /*: Observable<JwtResponse>*/ {
    return this._httpClient
      .post<JwtResponse>(this.AUTH_URI + '/login', {
        username,
        password,
        rememberMe,
        institutionId
      })
      .pipe(
        tap({
          next: response => {
            this.authenticatedSubject.next(true);
            this.sessionExpirationTime = new Date(response.expiresIn).getTime();

            this.startSession(this.sessionExpirationTime); // Set session expiration time

            this.fetchUserInfo().subscribe({
              next: () => {
                this._router.navigate(['/home/ble-init']);
                //this._router.navigate(['/auth/profiles']); para saltar el bluetooth y probar juegos -Ángela
              },
              error: () => {
                this._router.navigate(['/auth/login']); //TODO redirect to login with internal error
              }
            });
          }
        })
      );
  }

  public logout(queryParams?: any): void {
    this._httpClient.post<void>(this.AUTH_URI + '/logout', {}).subscribe({
      next: () => {
        this.userSubject.next(null);
        this.authenticatedSubject.next(false);

        this.clearSessionTimers();
        this._router.navigate(['/auth/login'], { queryParams: queryParams });
      },
      error: () => {
        this.userSubject.next(null);
        this.authenticatedSubject.next(false);

        this.clearSessionTimers();
        this._router.navigate(['/auth/login'], { queryParams: queryParams });
      }
    });
  }

  public refreshToken(): Observable<JwtResponse> {
    return this._httpClient.post<JwtResponse>(this.AUTH_URI + '/refresh-token', {}).pipe(
      tap({
        next: response => {
          this.sessionExpirationTime = new Date(response.expiresIn).getTime();
          this.resetSessionTimers(this.sessionExpirationTime); // Set session expiration time

          this.fetchUserInfo().subscribe({
            error: () => {
              this._router.navigate(['/auth/login']); //TODO redirect to login with internal error
            }
          });
        },
        error: () => {
          this.logout();
        }
      })
    );
  }

  public fetchUserInfo(): Observable<User | null> {
    return this._httpClient.get<any>(this.AUTH_URI + '/userinfo').pipe(
      map(response => {
        return response;
      }),
      tap({
        next: (user: User) => {
          this.userSubject.next(user);
        },

        error: () => {
          this.userSubject.next(null);
        }
      })
    );
  }

  public getLoggedUser(forceReload = false): Observable<User | null> {
    if (!this.userSubject.getValue() || forceReload) {
      return this.fetchUserInfo();
    }

    return this.user$;
  }

  public isAuthenticated(): Observable<boolean> {
    if (this.authenticatedSubject.getValue()) {
      return of(true);
    } else {
      return this.checkSessionExpiration().pipe(
        map(() => {
          this.authenticatedSubject.next(true);
          return true;
        }),
        catchError(() => {
          this.authenticatedSubject.next(false);
          return of(false);
        })
      );
    }
  }

  public getAuthorities(): Observable<string[]> {
    return this.user$.pipe(map((user: User | null) => user?.roles?.map(role => role.name) ?? []));
  }

  public isPasswordOutdated(): Observable<boolean> {
    return of(false);
    /*return this.fetchUserInfo().pipe(
      map((user: User | null) => {
        if (user) {
          return user.lastPasswordChange ? false : true;
        } else {
          return true;
        }
      })
    );*/
  }

  public changePassword(actualPassword: string, password: string): Observable<void> {
    return this._httpClient.post<void>(this.AUTH_URI + '/change-password', { actualPassword, password });
  }

  startSession(expirationTimestamp: number) {
    this.sessionExpirationTime = expirationTimestamp;
    this.scheduleSessionTimers();
  }

  private scheduleSessionTimers() {
    const currentTime = new Date().getTime();
    const remainingTime = this.sessionExpirationTime - currentTime;

    if (remainingTime > this.SESSION_TIMEOUT_WARNING) {
      this.warningTimeout = setTimeout(() => {
        this.openSessionExpireModal(this.SESSION_TIMEOUT_WARNING);
      }, remainingTime - this.SESSION_TIMEOUT_WARNING);
    }

    this.logoutTimeout = setTimeout(() => {
      this.logout();
    }, remainingTime);
  }

  clearSessionTimers() {
    clearTimeout(this.warningTimeout);
    clearTimeout(this.logoutTimeout);
  }

  resetSessionTimers(newExpiration: number) {
    this.clearSessionTimers();
    this.startSession(newExpiration);
  }

  openSessionExpireModal(remainingTime: number): void {
    const dialogRef = this._dialog.open(SessionWarningComponent, {
      width: '500px',
      disableClose: true,
      data: {
        timeLeft: remainingTime
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'extend') {
        this.refreshToken().subscribe({
          next: () => {}
        });
      } else {
        this.logout();
      }
    });
  }

  checkSessionExpiration(): Observable<any> {
    return this._httpClient.get<any>(this.AUTH_URI + '/session').pipe(
      tap({
        next: response => {
          this.authenticatedSubject.next(true);
          this.sessionExpirationTime = new Date(response.expiresIn).getTime();
          this.resetSessionTimers(this.sessionExpirationTime); // Set session expiration time
        },
        error: () => {
          this.logout();
        }
      })
    );
  }
}
